// Compute the "live" passenger count for a booking:
// sum of (car capacity × quantity) across every route/car in the booking.
//
// The capacityMap comes from `GET /api/cars` and is keyed by car_type
// ("lexus", "gmc", "staria", …). Falls back to booking.passengers when the
// map is empty or no cars match — so if the network call fails we still
// show *something* instead of `—`.
export function computeSeats(booking, capacityMap) {
  if (!booking) return 0;
  if (!capacityMap || Object.keys(capacityMap).length === 0) {
    return Number(booking.passengers || 0);
  }
  let total = 0;
  let hit = false;
  const routes = Array.isArray(booking.routes) ? booking.routes : [];
  for (const r of routes) {
    const cars = Array.isArray(r?.cars) ? r.cars : [];
    for (const c of cars) {
      const cap = capacityMap[c.car_type];
      if (cap && cap > 0) {
        total += cap * Number(c.quantity || 1);
        hit = true;
      }
    }
  }
  if (hit) return total;
  // Legacy single-car bookings that don't carry routes[].
  const cap = capacityMap[booking.car_type];
  const qty = Number(
    booking.quantity
      || (Array.isArray(booking.cars) && booking.cars[0]?.quantity)
      || 1,
  );
  if (cap && cap > 0) return cap * qty;
  return Number(booking.passengers || 0);
}

// Build the capacity map used by `computeSeats` from a raw /api/cars response.
export function buildCapacityMap(carsList) {
  const map = {};
  for (const c of carsList || []) {
    const key = c?.id || c?.car_type;
    const cap = Number(c?.capacity ?? c?.max_passengers ?? 0);
    if (key && cap > 0) map[key] = cap;
  }
  return map;
}

// ─── Persistent cache ──────────────────────────────────────────────────
// The /api/cars endpoint on production is ~8 MB (base64 car images inline),
// so it can take several seconds to load. We cache the *capacity subset* in
// localStorage so the very first render — and any deep link into a booking —
// has a usable map immediately, without waiting for the network round-trip.
const STORAGE_KEY = "mz_cars_capacity_v1";

export function loadCachedCapacityMap() {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch {
    return {};
  }
}

export function saveCachedCapacityMap(map) {
  try {
    if (typeof window === "undefined") return;
    if (!map || Object.keys(map).length === 0) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage full or disabled — safe to ignore.
  }
}
